import { buildPaymentFooterDisplayLines } from '@/utils/paymentFooterBankFields'
import { effectiveBodyFontFamily } from '@/utils/printFontFamily'

/** el-select 不能用空字符串作 option value，用此占位再在读写时映射为 '' */
export const STATEMENT_DUAL_GRID_UNBOUND = '__unbound__'

/** 账单签名：左送货方 / 右收货方 四行双列 */
export const STATEMENT_DUAL_GRID_DATA_KEYS = [
  { value: STATEMENT_DUAL_GRID_UNBOUND, label: '（不绑定，仅留空或固定文字）' },
  { value: 'customer_full_name', label: '客户全名（收货方）' },
  { value: 'party_name', label: '往来单位名称' },
  { value: 'creator_name', label: '制表人' },
  { value: 'salesperson_name', label: '销售员' },
  { value: 'statement_date', label: '账单日期' },
  { value: 'statement_no', label: '账单编号' }
]

export const DEFAULT_STATEMENT_DUAL_GRID = {
  enabled: true,
  left: [
    { id: 'sl_shipper', label: '送货方', dataKey: '', fixedName: '东莞市盈旺达新材料有限公司', blankSignature: false },
    { id: 'sl_seal', label: '盖章', dataKey: '', fixedName: '', blankSignature: false },
    { id: 'sl_maker', label: '制表', dataKey: 'creator_name', fixedName: '', blankSignature: false },
    { id: 'sl_date', label: '日期', dataKey: 'statement_date', fixedName: '', blankSignature: false }
  ],
  right: [
    { id: 'sr_receiver', label: '收货方', dataKey: 'customer_full_name', fixedName: '', blankSignature: false },
    { id: 'sr_seal', label: '盖章', dataKey: '', fixedName: '', blankSignature: false },
    { id: 'sr_sign', label: '签字', dataKey: '', fixedName: '', blankSignature: false },
    { id: 'sr_date', label: '日期', dataKey: 'statement_date', fixedName: '', blankSignature: false }
  ]
}

export function statementDualGridDataKeyForUi (dataKey) {
  const k = (dataKey ?? '').trim()
  return k || STATEMENT_DUAL_GRID_UNBOUND
}

export function statementDualGridDataKeyFromUi (uiValue) {
  const v = (uiValue ?? '').trim()
  return v === STATEMENT_DUAL_GRID_UNBOUND ? '' : v
}

function normalizeDualCell (raw, idx, side) {
  const dk = statementDualGridDataKeyFromUi(raw?.dataKey)
  return {
    id: raw?.id || `${side}_${idx}`,
    label: (raw?.label || '').trim(),
    dataKey: dk,
    fixedName: (raw?.fixedName || '').trim(),
    blankSignature: Boolean(raw?.blankSignature)
  }
}

export function normalizeStatementDualGrid (grid) {
  const g = grid && typeof grid === 'object' ? grid : {}
  const left = Array.isArray(g.left) ? g.left : DEFAULT_STATEMENT_DUAL_GRID.left
  const right = Array.isArray(g.right) ? g.right : DEFAULT_STATEMENT_DUAL_GRID.right
  return {
    enabled: g.enabled !== false,
    left: left.map((c, i) => normalizeDualCell(c, i, 'left')),
    right: right.map((c, i) => normalizeDualCell(c, i, 'right'))
  }
}

export function statementDualGridRowPairs (grid) {
  const g = normalizeStatementDualGrid(grid)
  const n = Math.max(g.left.length, g.right.length, 1)
  return Array.from({ length: n }, (_, i) => ({
    left: g.left[i] || { id: `left_${i}`, label: '', dataKey: '', fixedName: '', blankSignature: false },
    right: g.right[i] || { id: `right_${i}`, label: '', dataKey: '', fixedName: '', blankSignature: false }
  }))
}

export function resolveStatementDualGridCellValue (cell, fetchFromData) {
  if (!cell) return ''
  if (cell.blankSignature) return ''
  const fixed = (cell.fixedName || '').trim()
  if (fixed) return fixed
  const k = (cell.dataKey || '').trim()
  if (!k || typeof fetchFromData !== 'function') return ''
  try {
    const v = fetchFromData(k)
    if (v === null || v === undefined) return ''
    return String(v)
  } catch {
    return ''
  }
}

export function formatDualGridLabel (label) {
  const t = (label || '').trim()
  if (!t) return ''
  return /[:：]$/.test(t) ? t : `${t}：`
}

export function useStatementDualSignatureGrid (signatures, templateType) {
  if (templateType !== 'payment_request') return false
  const sig = signatures || {}
  if (sig.layout === 'spread') return false
  if (sig.layout === 'statement_dual_grid') {
    return normalizeStatementDualGrid(sig.statementDualGrid).enabled
  }
  return false
}

export function paymentFooterBorderEnabled (paymentFooter) {
  if (!paymentFooter) return false
  return paymentFooter.showBorder !== false
}

/** 收款表格「标签」列宽占比（10～60），默认 22，避免地址等长内容被挤成两行 */
export function paymentFooterLabelColWidthPercent (paymentFooter) {
  const n = Number(paymentFooter?.labelColWidthPercent)
  if (Number.isFinite(n) && n >= 10 && n <= 60) return Math.round(n)
  return 22
}

/** 收款信息行高倍数，默认 1.55 */
export function clampPaymentFooterLineHeight (raw, fallback = 1.55) {
  const n = Number(raw)
  if (!Number.isFinite(n) || n <= 0) return fallback
  return Math.min(2.5, Math.max(1, n))
}

/** 收款信息字号 px，默认 12 */
export function paymentFooterFontSizePx (paymentFooter, fallback = 12) {
  const n = Number(paymentFooter?.fontSizePx)
  if (Number.isFinite(n) && n >= 8 && n <= 24) return Math.round(n)
  return fallback
}

/** 收款信息字体；空则跟随页面正文 defaultFontFamily */
export function resolvePaymentFooterFontFamily (paymentFooter, page, templateType) {
  const raw = paymentFooter?.fontFamily
  if (raw != null && String(raw).trim() !== '') return String(raw).trim()
  return effectiveBodyFontFamily(page, templateType)
}

export function paymentFooterTypographyVars (paymentFooter, page, templateType) {
  return {
    fontSizePx: paymentFooterFontSizePx(paymentFooter),
    lineHeight: clampPaymentFooterLineHeight(paymentFooter?.lineHeight),
    fontFamily: resolvePaymentFooterFontFamily(paymentFooter, page, templateType)
  }
}

export function paymentFooterTypographyInlineStyle (paymentFooter, page, templateType) {
  const { fontSizePx, lineHeight, fontFamily } = paymentFooterTypographyVars(
    paymentFooter,
    page,
    templateType
  )
  return `font-size:${fontSizePx}px;line-height:${lineHeight};font-family:${fontFamily}`
}

export function paymentFooterBlockCssStyle (paymentFooter, page, templateType, marginTopPx) {
  const { fontSizePx, lineHeight, fontFamily } = paymentFooterTypographyVars(
    paymentFooter,
    page,
    templateType
  )
  return {
    marginTop: `${Math.max(0, Number(marginTopPx) || 0)}px`,
    fontSize: `${fontSizePx}px`,
    lineHeight: String(lineHeight),
    fontFamily
  }
}

export function buildPaymentFooterDisplayRows (pf, resolveField) {
  const notice = (pf?.noticeText || '请将以上货款汇入以下银行账户').trim()
  const lines = buildPaymentFooterDisplayLines(pf || {}, resolveField)
  return { notice, lines }
}

export function escapeHtmlText (s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

export function buildPaymentFooterHtml (pf, resolveField, opts = {}) {
  const { notice, lines } = buildPaymentFooterDisplayRows(pf, resolveField)
  const page = opts.page || {}
  const templateType = opts.templateType || 'payment_request'
  const typoStyle = paymentFooterTypographyInlineStyle(pf, page, templateType)
  const useBorder = paymentFooterBorderEnabled(pf)
  if (!useBorder) {
    const body = lines
      .map((l) => `<div>${escapeHtmlText(l.label)}${escapeHtmlText(l.value)}</div>`)
      .join('')
    return `<div class="pure-print-payment" style="${typoStyle}"><div class="t">${escapeHtmlText(notice)}</div>${body}</div>`
  }
  const lw = paymentFooterLabelColWidthPercent(pf)
  const rows = lines
    .map(
      (l) =>
        `<tr><td class="pf-label">${escapeHtmlText(l.label)}</td><td class="pf-value">${escapeHtmlText(l.value)}</td></tr>`
    )
    .join('')
  return `<div class="pure-print-payment pure-print-payment--bordered" style="${typoStyle}"><div class="t">${escapeHtmlText(notice)}</div><table class="print-payment-footer__table" cellspacing="0" cellpadding="0"><colgroup><col style="width:${lw}%" /><col /></colgroup><tbody>${rows}</tbody></table></div>`
}

export function buildStatementDualGridHtml (signatures, fetchFromData, escapeHtml = escapeHtmlText) {
  const grid = normalizeStatementDualGrid(signatures?.statementDualGrid)
  const pairs = statementDualGridRowPairs(grid)
  const rows = pairs
    .map((row) => {
      const lLabel = formatDualGridLabel(row.left.label)
      const rLabel = formatDualGridLabel(row.right.label)
      const lVal = resolveStatementDualGridCellValue(row.left, fetchFromData)
      const rVal = resolveStatementDualGridCellValue(row.right, fetchFromData)
      return `<tr>
        <td class="sig-cell sig-cell--left"><span class="sig-lbl">${escapeHtml(lLabel)}</span><span class="sig-val">${escapeHtml(lVal)}</span></td>
        <td class="sig-cell sig-cell--right"><span class="sig-lbl">${escapeHtml(rLabel)}</span><span class="sig-val">${escapeHtml(rVal)}</span></td>
      </tr>`
    })
    .join('')
  return `<div class="pure-print-signs pure-print-signs--dual-grid"><table class="print-signature-dual-grid" cellspacing="0" cellpadding="0"><tbody>${rows}</tbody></table></div>`
}
